import * as React from 'react';
import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHeadset } from '@fortawesome/free-solid-svg-icons';
// @ts-ignore
import style from './Messages.module.scss?module';
import LoadingSpinner from '../../../../../Abstracts/LoadingSpinner/LoadingSpinner';
import { useUser } from '../../../../../Context/UserContext';
import useSupportChatThread from './useSupportChatThread';
import MessageBubble from './MessageBubble';
import Composer from './Composer';

const FAQ_PATH = '/virtual-trade-show/help-center/faq';

const firstName = (fullName: string | undefined): string => {
  if (!fullName) return '';
  return fullName.trim().split(/\s+/)[0] ?? '';
};

export default function Messages({ isActive = true }: { isActive?: boolean }) {
  const { thread, isLoading, sendMessage, markAsRead, isSending, sendError } = useSupportChatThread(isActive);
  const { user } = useUser();
  const listRef = useRef<HTMLDivElement>(null);
  const lastMessageIdRef = useRef<number | null>(null);
  const lastAckedRef = useRef<string | null>(null);
  const greetingName = firstName(user?.name);

  useEffect(() => {
    if (!thread) return;
    const lastMessage = thread.messages[thread.messages.length - 1];
    const lastId = lastMessage ? lastMessage.id : null;
    if (lastId !== lastMessageIdRef.current) {
      lastMessageIdRef.current = lastId;
      if (listRef.current) {
        listRef.current.scrollTop = listRef.current.scrollHeight;
      }
    }
  }, [thread]);

  useEffect(() => {
    if (!thread) {
      // Reset ack when the thread is gone (archived, logged out, …) so the
      // next freshly opened thread will be acked once.
      lastAckedRef.current = null;
      return;
    }
    if (!isActive) return;
    if (!thread.customer_unread) return;
    if (thread.last_message_at === lastAckedRef.current) return;

    const ackTarget = thread.last_message_at;
    // Set the ref BEFORE awaiting — two effects firing close together would
    // otherwise both clear the dedup check and issue duplicate POSTs. Roll
    // back the ref if the request fails so the next tick can retry.
    const previous = lastAckedRef.current;
    lastAckedRef.current = ackTarget;
    markAsRead().catch(() => {
      lastAckedRef.current = previous;
    });
  }, [isActive, thread, markAsRead]);

  if (isLoading && !thread) {
    return (
      <div className={style.Loading}>
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className={style.Messages}>
      <div className={style.Header}>
        <div className={style.HeaderIcon}>
          <FontAwesomeIcon icon={faHeadset} />
        </div>
        <div className={style.HeaderText}>
          <div className={style.HeaderTitle}>
            Naskorsports Customer Service
          </div>
          <div className={style.HeaderSubtitle}>
            Usually replies the same day. Or call{' '}
            <a href="tel:+31777201200">+31 777 201 200</a>.
          </div>
        </div>
      </div>
      <div className={style.List} ref={listRef}>
        {!thread || thread.messages.length === 0 ? (
          <div className={style.Empty}>
            <h3>
              {greetingName ? `Hi ${greetingName}, ` : ''}how can we help?
            </h3>
            <p>
              Write your question to us — we'll reply as soon as possible right
              here in this window. You can also attach images.
            </p>
            <p className={style.EmptyHint}>
              Tip: Many questions are already answered in our{' '}
              <Link to={FAQ_PATH}>FAQ</Link>.
            </p>
          </div>
        ) : (
          thread.messages.map((message) => (
            <MessageBubble key={message.id} message={message} />
          ))
        )}
      </div>
      <Composer onSubmit={sendMessage} isSending={isSending} sendError={sendError} />
    </div>
  );
}
